<script setup lang="ts">
import type { PathwayEvent, Step } from '@/types/shared/pathway-event'
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import PathwayEventStep from '@/components/PathwayEventStep.vue'
import { computed, ref } from 'vue'
import { SPECIAL_ECOMMERCE_ITEM_ID } from '@/types/shared/player-config'
import { useConfig } from '@/composables/game/config'
import BaseButton from '@/components/base/BaseButton.vue'
import PaymentButtons from '@/components/PaymentButtons.vue'
import { useEcommercePrice } from '@/composables/game/ecommerce'

const props = defineProps<{
	show: boolean
	step: Step & { index: number, originalIndex: number }
	pathwayEvent: PathwayEvent
}>()
const emit = defineEmits<{
	(e: 'hide'): void
	(e: 'purchaseDone'): void
}>()

const { config } = useConfig()
const { getStarPrice } = useEcommercePrice()

const maybeEcommerceItem = computed(() => config.value.game.ecommerce.items.find(item => item.id === `${SPECIAL_ECOMMERCE_ITEM_ID.PATHWAY_STEP}.${props.pathwayEvent.id}`))
const ecommerceItem = computed(() => {
  if (!maybeEcommerceItem.value) throw new Error('Ecommerce item not found with the id: ' + `${SPECIAL_ECOMMERCE_ITEM_ID.PATHWAY_STEP}.${props.pathwayEvent.id}`)
  return maybeEcommerceItem.value
})

const starPrice = computed(() => getStarPrice(ecommerceItem.value))
const purchased = ref(false)

function handlePurchase(paid: boolean) {
  if (paid) {
    purchased.value = true
  }
}

function onClose() {
  if (purchased.value) {
    emit('purchaseDone')
  }
  emit('hide')
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    bg-fade-class="z-[802]"
    wrapper-class="z-[802]"
    content-class="pt-6"
    @hide="onClose"
  >
    <div
      class="w-full relative"
      :class="step.index === 0 ? 'pt-[60px]' : 'pt-6'"
    >
      <StarsBalancePill
        class="absolute left-0 top-0"
      />
      <PathwayEventStep
        class="mx-auto max-w-fit"
        :event-id="pathwayEvent.id"
        inner-container-class="px-[29px] pt-0 pb-[6px] justify-center"
        :main-color="pathwayEvent.mainColor"
        :step="step"
        only-visual
        :has-arrow="false"
        is-claimable
        :multi-reward-support="step.index === 0"
      />
      <Transition
        name="fade"
        mode="out-in"
      >
        <div
          v-if="purchased"
          class="mt-3 w-full flex flex-col items-center"
        >
          <div class="text-center text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
            Purchase successful!
          </div>
          <div class="text-center text-white/[.60] text-sm font-medium">
            {{ pathwayEvent.eventName }} Step #{{ step.originalIndex }}
          </div>
          <BaseButton
            class="mt-12 w-full"
            @click="onClose"
          >
            Close
          </BaseButton>
        </div>
        <div
          v-else
          class="mt-6 w-full flex flex-col items-center"
        >
          <div class="text-center text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
            {{ pathwayEvent.eventName }} Step #{{ step.originalIndex }} = ${{ starPrice?.usdAmount.toFixed(2) }}
          </div>

          <div class="mt-12 w-full">
            <PaymentButtons
              :item="ecommerceItem"
              @purchase="({ paid }) => handlePurchase(paid)"
            />
          </div>
        </div>
      </Transition>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

</style>